/**
 * derive — the bridge between the mirrored pop.in engine (rich ~50-token themes)
 * and the partner portal's world (a Theme is 6 hex tokens + logo).
 *
 * A THEME RECIPE is the pop.in "mode": seed × polarity × treatment × font. The
 * portal keeps the recipe draft-side (there is no backend field for it yet —
 * WS3.1) and, on publish, writes the *derived* 6 legacy hexes into the existing
 * Theme record. A v2-aware embed regenerates the full theme from the recipe; a
 * v2-unaware embed still gets a faithful 6-hex fallback. Same engine both ways.
 *
 * POLARITY NOTE (flagged for AJ): in today's registry polarity is not orthogonal
 * to treatment — every reflective treatment is `light` and only NIGHT is `dark`,
 * so `recipe.polarity` always equals `TREATMENTS[treatmentId].polarity`. The knob
 * therefore *filters* which treatments are on offer rather than mutating an
 * arbitrary treatment. We keep polarity in the recipe because the v2 message
 * carries it and because an emissive-light / reflective-dark treatment could land
 * upstream later.
 */
import { buildCellTheme, TREATMENTS, type Treatment } from "./treatments";
import { hexToOklch, type Appearance } from "./anchor-generate";
import { DEFAULT_FONT_ID } from "./fonts";

export type Polarity = Appearance; // "light" | "dark"

/** The pop.in mode: what the Theme Studio knobs produce. */
export interface ThemeRecipe {
  /** A key of {@link TREATMENTS}, e.g. "NIGHT" | "DAYLIGHT-LCD". */
  treatmentId: string;
  /** The brand identity color, `#rrggbb`. Pinned at the engine's ramp step 9. */
  seed: string;
  /** "light" | "dark". Today always equals the treatment's own polarity. */
  polarity: Polarity;
  /** A key of `FONT_CHOICES`, e.g. "default" | "mono". */
  fontId: string;
}

/** The 6 legacy color tokens a portal Theme stores (all `#rrggbb`). */
export interface LegacyHexes {
  header: string;
  background: string;
  surface: string;
  primary: string;
  accent: string;
  text: string;
}

/** Ordered treatment list for the UI (NIGHT first — the pop.in signature). */
export const TREATMENT_ORDER = ["NIGHT", "DAYLIGHT-LCD", "DAYLIGHT-GREEN", "PAPER-EINK"] as const;

export const TREATMENT_LIST: Treatment[] = TREATMENT_ORDER.map((id) => TREATMENTS[id]!);

export function getTreatment(treatmentId: string): Treatment {
  const t = TREATMENTS[treatmentId];
  if (!t) throw new Error(`Unknown treatment "${treatmentId}"`);
  return t;
}

export function treatmentsForPolarity(polarity: Polarity): Treatment[] {
  return TREATMENT_LIST.filter((t) => t.polarity === polarity);
}

/** Normalise a hex seed to opaque `#rrggbb` (expands `#rgb`); throws on garbage. */
export function normalizeSeed(hex: string): string {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) throw new Error(`Invalid seed hex "${hex}"`);
  return `#${h.toLowerCase()}`;
}

/**
 * Run the real engine for a recipe and project its ~50 semantic tokens onto the
 * portal's 6. `accent` maps to the anchor's brighter/deeper sibling (color10) so
 * it reads as a distinct highlight rather than a duplicate of `primary`.
 */
export function deriveLegacyHexes(tokens: Record<string, string>): LegacyHexes {
  return {
    header: tokens.headerBackground ?? tokens.surfaceBackground ?? tokens.background!,
    background: tokens.surfaceBackground ?? tokens.background!,
    surface: tokens.surfaceCard ?? tokens.color3!,
    primary: tokens.actionPrimary ?? tokens.color9!,
    accent: tokens.color10 ?? tokens.brandAccent ?? tokens.actionPrimary!,
    text: tokens.textDefault ?? tokens.color12!,
  };
}

/** Full engine token record for a recipe (what a v2 embed rebuilds from). */
export function buildRecipeTokens(recipe: ThemeRecipe): Record<string, string> {
  return buildCellTheme(normalizeSeed(recipe.seed), getTreatment(recipe.treatmentId));
}

/** The portal's 6 hexes for a recipe — engine-generated, WCAG-AA gated. */
export function recipeToLegacyHexes(recipe: ThemeRecipe): LegacyHexes {
  return deriveLegacyHexes(buildRecipeTokens(recipe));
}

/** A generated theme option shown "chip-less" on the live app. */
export interface ThemeCandidate {
  /** Stable id for React keys / analytics (recipe-derived). */
  id: string;
  /** Human label (the treatment's name). */
  label: string;
  /** The treatment's one-line spec. */
  spec: string;
  recipe: ThemeRecipe;
  hexes: LegacyHexes;
}

function candidateId(recipe: ThemeRecipe): string {
  return `${recipe.treatmentId}:${recipe.seed}:${recipe.fontId}`;
}

function makeCandidate(recipe: ThemeRecipe): ThemeCandidate {
  const t = getTreatment(recipe.treatmentId);
  return { id: candidateId(recipe), label: t.name, spec: t.spec, recipe, hexes: recipeToLegacyHexes(recipe) };
}

/** Build one candidate from an explicit recipe (seed already chosen). */
export function candidateFromRecipe(recipe: ThemeRecipe): ThemeCandidate {
  return makeCandidate(recipe);
}

export interface GenerateOptions {
  /** Bias the ordering so this polarity leads (e.g. a URL-ingest suggestion). */
  polarity?: Polarity;
  fontId?: string;
  /** How many candidates (default 3, clamped 1..TREATMENT_LIST.length). */
  limit?: number;
}

/**
 * "Give us anything → 2-3 complete candidates." Fans one seed across treatments,
 * leading with the suggested polarity, always spanning at least one dark + one
 * light option for range. Each candidate is a full recipe + engine-derived hexes.
 */
export function generateCandidatesFromSeed(seedInput: string, opts: GenerateOptions = {}): ThemeCandidate[] {
  const seed = normalizeSeed(seedInput);
  const fontId = opts.fontId ?? DEFAULT_FONT_ID;
  const limit = Math.max(1, Math.min(opts.limit ?? 3, TREATMENT_LIST.length));

  // Order treatments so the suggested polarity leads; keep the curated order within.
  const ordered = [...TREATMENT_LIST].sort((a, b) => {
    if (opts.polarity) {
      const aLead = a.polarity === opts.polarity ? 0 : 1;
      const bLead = b.polarity === opts.polarity ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
    }
    return TREATMENT_ORDER.indexOf(a.id as never) - TREATMENT_ORDER.indexOf(b.id as never);
  });

  const chosen = ordered.slice(0, limit);
  // Guarantee range: if all chosen share a polarity, swap the last for the other.
  const polarities = new Set(chosen.map((t) => t.polarity));
  if (polarities.size === 1 && limit > 1) {
    const other = ordered.find((t) => !polarities.has(t.polarity));
    if (other) chosen[chosen.length - 1] = other;
  }

  return chosen.map((t) => makeCandidate({ treatmentId: t.id, seed, polarity: t.polarity, fontId }));
}

/**
 * Turn an ingested 6-hex palette (from `generateThemeColorsFromUrl` / a logo)
 * into a SEED + polarity suggestion — the P2 upgrade ("emit a seed, not raw
 * hexes"). Seed = the most chromatic of primary/accent; polarity = the ground's
 * perceptual lightness.
 */
export function seedFromPalette(p: {
  primary?: string | null;
  accent?: string | null;
  background?: string | null;
}): { seed: string; polarity: Polarity } {
  const candidates = [p.primary, p.accent].filter((c): c is string => !!c);
  let seed = candidates[0] ?? "#7c3aed";
  let bestChroma = -1;
  for (const c of candidates) {
    try {
      const chroma = hexToOklch(normalizeSeed(c)).C;
      if (chroma > bestChroma) { bestChroma = chroma; seed = c; }
    } catch { /* skip unparseable */ }
  }
  let polarity: Polarity = "dark";
  try {
    if (p.background) polarity = hexToOklch(normalizeSeed(p.background)).L >= 0.5 ? "light" : "dark";
  } catch { /* default dark */ }
  return { seed: normalizeSeed(seed), polarity };
}
